import { Textarea, Checkbox, ActionIcon, Paper, Box, Title, Stack, Button, Group, Text, Image, FileInput, Tabs, TextInput } from '@mantine/core';
import { IconAlertTriangle, IconSquareCheck, IconArrowUp, IconArrowDown, IconTrash, IconPlus, IconPhoto, IconX, IconNotes, IconBuilding } from '@tabler/icons-react';
import { MeasuresProps } from './types';
import { useState, useEffect } from 'react';
import { notifications } from '@mantine/notifications';
import { deleteMassnahme } from '../../lib/pocketbase';
import { MeasureService } from '../../services/MeasureService';

export function Measures({ measures, setMeasures, defaultMassnahme }: MeasuresProps) {
  const [fileUrls, setFileUrls] = useState<{ [key: string]: string }>({});
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [deletedMeasureIds, setDeletedMeasureIds] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<string[]>(measures.map(() => 'ist-zustand'));

  useEffect(() => {
    // Cleanup file URLs on unmount
    return () => {
      Object.values(fileUrls).forEach(url => URL.revokeObjectURL(url));
    };
  }, [fileUrls]);

  const addEntry = () => {
    setMeasures([...measures, { 
      ...defaultMassnahme, 
      reihenfolge: measures.length + 1 
    }]);
    setActiveTab([...activeTab, 'ist-zustand']);
  };

  const removeEntry = async (index: number) => {
    if (measures.length <= 1) return;
    
    const measure = measures[index];
    const updatedMeasures = measures.filter((_, i) => i !== index);
    
    // If measure has an ID, add it to deletedMeasureIds and delete from database
    if (measure.id) {
      try {
        await deleteMassnahme(measure.id);
        setDeletedMeasureIds(prev => [...prev, measure.id]);
      } catch (error) {
        console.error('Failed to delete measure:', error);
        notifications.show({
          title: 'Fehler',
          message: 'Massnahme konnte nicht gelöscht werden',
          color: 'red'
        });
        return; // Don't update UI if delete failed
      }
    }
    
    setMeasures(updatedMeasures.map((measure, idx) => ({
      ...measure,
      reihenfolge: idx + 1
    })));

    // Update active tabs
    const newActiveTabs = [...activeTab];
    newActiveTabs.splice(index, 1);
    setActiveTab(newActiveTabs);
  };

  const moveEntry = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= measures.length) return;
    
    const updatedMeasures = [...measures];
    [updatedMeasures[index], updatedMeasures[newIndex]] = [updatedMeasures[newIndex], updatedMeasures[index]];
    
    setMeasures(updatedMeasures.map((measure, idx) => ({
      ...measure,
      reihenfolge: idx + 1
    })));

    // Update active tabs
    const newActiveTabs = [...activeTab];
    [newActiveTabs[index], newActiveTabs[newIndex]] = [newActiveTabs[newIndex], newActiveTabs[index]];
    setActiveTab(newActiveTabs);
  };

  const updateEntry = (index: number, field: keyof typeof defaultMassnahme, value: any) => {
    setMeasures(measures.map((item, i) => 
      i === index ? { ...item, [field]: value } : item
    ));
  };

  const handleFileUpload = (files: File[] | null, index: number) => {
    if (!files) return;
    
    const currentMeasure = measures[index];
    const currentFiles = currentMeasure.anhaenge || [];
    const totalFiles = currentFiles.length + (currentMeasure.existingAnhaenge?.length || 0);
    
    const remainingSlots = 3 - totalFiles;
    if (remainingSlots <= 0) {
      notifications.show({
        title: 'Hinweis',
        message: 'Maximale Anzahl von Anhängen (3) erreicht',
        color: 'yellow'
      });
      return;
    }

    const newFiles = files.slice(0, remainingSlots);
    
    // Validate file types
    const invalidFiles = newFiles.filter(file => !file.type.startsWith('image/'));
    if (invalidFiles.length > 0) {
      notifications.show({
        title: 'Fehler',
        message: 'Nur Bilddateien sind erlaubt',
        color: 'red'
      });
      return;
    }

    // Validate file sizes (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    const oversizedFiles = newFiles.filter(file => file.size > maxSize);
    if (oversizedFiles.length > 0) {
      notifications.show({
        title: 'Fehler',
        message: 'Dateien dürfen nicht größer als 5MB sein',
        color: 'red'
      });
      return;
    }
    
    const newUrls: { [key: string]: string } = {};
    newFiles.forEach(file => {
      const url = URL.createObjectURL(file);
      newUrls[file.name] = url;
    });
    
    setFileUrls(prev => ({ ...prev, ...newUrls }));
    updateEntry(index, 'anhaenge', [...currentFiles, ...newFiles]);

    notifications.show({
      title: 'Erfolg',
      message: `${newFiles.length} Datei${newFiles.length !== 1 ? 'en' : ''} hochgeladen`,
      color: 'green'
    });
  };

  const removeFile = (index: number, fileIndex: number, isExisting: boolean) => {
    const measure = measures[index];
    
    if (isExisting) {
      // Create a new array without the removed file
      const existingFiles = [...(measure.existingAnhaenge || [])];
      existingFiles.splice(fileIndex, 1);
      
      // Update the measure with the new array
      setMeasures(measures.map((item, i) => 
        i === index 
          ? { 
              ...item, 
              existingAnhaenge: existingFiles,
              // Ensure anhaenge array exists
              anhaenge: item.anhaenge || []
            } 
          : item
      ));
      
      notifications.show({
        title: 'Erfolg',
        message: 'Anhang wurde entfernt',
        color: 'green'
      });
    } else {
      const currentFiles = [...(measure.anhaenge || [])];
      const removedFile = currentFiles[fileIndex];
      if (removedFile && fileUrls[removedFile.name]) {
        URL.revokeObjectURL(fileUrls[removedFile.name]);
        setFileUrls(prev => {
          const newUrls = { ...prev };
          delete newUrls[removedFile.name];
          return newUrls;
        });
      }
      currentFiles.splice(fileIndex, 1);
      
      // Update the measure with the new array
      setMeasures(measures.map((item, i) => 
        i === index 
          ? { 
              ...item, 
              anhaenge: currentFiles,
              // Ensure existingAnhaenge array exists
              existingAnhaenge: item.existingAnhaenge || []
            } 
          : item
      ));

      notifications.show({
        title: 'Erfolg',
        message: 'Anhang wurde entfernt',
        color: 'green'
      });
    }
  };

  const getFilePreview = (file: File) => {
    return fileUrls[file.name] || URL.createObjectURL(file);
  };

  return (
    <Box>
      <Title order={3} mb="md" display="flex" style={{ alignItems: 'center', gap: '0.5rem' }}>
        <IconSquareCheck size={20} />
        Ist-Zustand und Massnahmen
      </Title>

      <Stack gap="md">
        {measures.map((entry, index) => (
          <Paper key={entry.id || index} shadow="xs" p="md" withBorder>
            <Box mb="md" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <Box
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: 'var(--mantine-color-gray-2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <span style={{ fontWeight: 700, fontSize: '0.875rem' }}>{entry.nummer || '-'}</span>
                </Box>
                <Title order={4}>Eintrag</Title>
              </Box>
              <Box style={{ display: 'flex', gap: '0.5rem' }}>
                <ActionIcon
                  variant="light"
                  onClick={() => moveEntry(index, 'up')}
                  disabled={index === 0}
                >
                  <IconArrowUp size={16} />
                </ActionIcon>
                <ActionIcon
                  variant="light"
                  onClick={() => moveEntry(index, 'down')}
                  disabled={index === measures.length - 1}
                >
                  <IconArrowDown size={16} />
                </ActionIcon>
                <ActionIcon
                  variant="light"
                  color="red"
                  onClick={() => removeEntry(index)}
                  disabled={measures.length === 1}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Box>
            </Box>

            <TextInput
              name={`measures.${index}.bereich`}
              label="Thema"
              placeholder="Geben Sie das Thema ein"
              leftSection={<IconBuilding size={16} />}
              value={entry.bereich}
              onChange={(e) => updateEntry(index, 'bereich', e.currentTarget.value)}
              required
              withAsterisk
              error={!entry.bereich ? 'Thema ist erforderlich' : null}
              mb="md"
            />

            <Tabs value={activeTab[index]} onChange={(value) => {
              const newActiveTabs = [...activeTab];
              newActiveTabs[index] = value || 'ist-zustand';
              setActiveTab(newActiveTabs);
            }}>
              <Tabs.List>
                <Tabs.Tab value="ist-zustand">Ist-Zustand</Tabs.Tab>
                <Tabs.Tab value="massnahme">Massnahme</Tabs.Tab>
                <Tabs.Tab value="bemerkung">Bemerkung</Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="ist-zustand">
                <Textarea
                  name={`measures.${index}.istzustand`}
                  placeholder="Beschreiben Sie den aktuellen Zustand (min. 10 Zeichen)"
                  minRows={4}
                  autosize
                  leftSection={<IconAlertTriangle size={16} />}
                  value={entry.istzustand}
                  onChange={(e) => updateEntry(index, 'istzustand', e.currentTarget.value)}
                  required
                  withAsterisk
                  error={
                    entry.istzustand && entry.istzustand.length > 0 && entry.istzustand.length < 10
                      ? `Noch ${10 - entry.istzustand.length} Zeichen erforderlich`
                      : entry.istzustand && entry.istzustand.length > 1000
                      ? `${entry.istzustand.length - 1000} Zeichen zu viel (max. 1000)`
                      : null
                  }
                  description={entry.istzustand ? `${entry.istzustand.length} / 1000 Zeichen` : ''}
                  mt="md"
                  styles={{
                    input: {
                      backgroundColor: '#fff3e6',
                    }
                  }}
                  aria-label="Ist-Zustand"
                  tabIndex={0}
                />
              </Tabs.Panel>

              <Tabs.Panel value="massnahme">
                <Textarea
                  name={`measures.${index}.massnahme`}
                  placeholder="Beschreiben Sie die erforderliche Massnahme (min. 10 Zeichen)"
                  minRows={4}
                  autosize
                  leftSection={<IconSquareCheck size={16} />}
                  value={entry.massnahme}
                  onChange={(e) => updateEntry(index, 'massnahme', e.currentTarget.value)}
                  required
                  withAsterisk
                  error={
                    entry.massnahme && entry.massnahme.length > 0 && entry.massnahme.length < 10
                      ? `Noch ${10 - entry.massnahme.length} Zeichen erforderlich`
                      : entry.massnahme && entry.massnahme.length > 1000
                      ? `${entry.massnahme.length - 1000} Zeichen zu viel (max. 1000)`
                      : null
                  }
                  description={entry.massnahme ? `${entry.massnahme.length} / 1000 Zeichen` : ''}
                  mt="md"
                  styles={{
                    input: {
                      backgroundColor: '#e6ffe6',
                    }
                  }}
                  aria-label="Massnahme"
                  tabIndex={0}
                />
              </Tabs.Panel>

              <Tabs.Panel value="bemerkung">
                <Textarea
                  name={`measures.${index}.bemerkung`}
                  placeholder="Fügen Sie eine Bemerkung hinzu"
                  minRows={4}
                  autosize
                  leftSection={<IconNotes size={16} />}
                  value={entry.bemerkung}
                  onChange={(e) => updateEntry(index, 'bemerkung', e.currentTarget.value)}
                  mt="md"
                  aria-label="Bemerkung"
                  tabIndex={0}
                />
              </Tabs.Panel>
            </Tabs>

            <Group mt="md">
              
              <Checkbox
                name={`measures.${index}.erledigt`}
                label="Erledigt"
                mt={30}
                checked={entry.erledigt}
                onChange={(e) => updateEntry(index, 'erledigt', e.currentTarget.checked)}
                tabIndex={0}
              />
            </Group>

            <Box mt="md">
              <Text size="sm" fw={500} mb="xs">Anhänge (max. 3 Bilder)</Text>
              
              {entry.existingAnhaenge?.length > 0 && (
                <Group mb="md">
                  {entry.existingAnhaenge.map((file, fileIndex) => (
                    <Box key={fileIndex} pos="relative">
                      <Image
                        src={MeasureService.getFileUrl(entry.id, file)}
                        width={100}
                        height={100}
                        radius="md"
                        fit="cover"
                        alt={`Anhang ${fileIndex + 1}`}
                      />
                      <ActionIcon
                        color="red"
                        variant="filled"
                        size="xs"
                        style={{ position: 'absolute', top: 5, right: 5 }}
                        onClick={() => removeFile(index, fileIndex, true)}
                        tabIndex={0}
                      >
                        <IconX size={10} />
                      </ActionIcon>
                    </Box>
                  ))}
                </Group>
              )}

              {entry.anhaenge?.length > 0 && (
                <Group mb="md">
                  {entry.anhaenge.map((file, fileIndex) => (
                    <Box key={fileIndex} pos="relative">
                      <Image
                        src={getFilePreview(file)}
                        width={100}
                        height={100}
                        radius="md"
                        fit="cover"
                        alt={`Neuer Anhang ${fileIndex + 1}`}
                      />
                      <ActionIcon
                        color="red"
                        variant="filled"
                        size="xs"
                        style={{ position: 'absolute', top: 5, right: 5 }}
                        onClick={() => removeFile(index, fileIndex, false)}
                        tabIndex={0}
                      >
                        <IconX size={10} />
                      </ActionIcon>
                    </Box>
                  ))}
                </Group>
              )}

              {(entry.anhaenge?.length || 0) + (entry.existingAnhaenge?.length || 0) < 3 && (
                <FileInput
                  name={`measures.${index}.anhaenge`}
                  accept="image/png,image/jpeg,image/jpg,image/gif,image/webp,image/heic,image/heif,image/*"
                  multiple
                  placeholder="Bilder auswählen (PNG, JPG, HEIC, etc.)"
                  leftSection={<IconPhoto size={16} />}
                  onChange={(files) => handleFileUpload(files, index)}
                  value={[]}
                  aria-label="Bilder auswählen"
                  tabIndex={0}
                />
              )}
            </Box>
          </Paper>
        ))}

        <Button
          variant="light"
          fullWidth
          leftSection={<IconPlus size={16} />}
          onClick={addEntry}
          tabIndex={0}
        >
          Neue Ist-Zustand / Massnahme hinzufügen
        </Button>
      </Stack>
    </Box>
  );
}
